// Mini modal with picture separated by a vertical line
import React, { useContext, useEffect, useRef } from 'react';
import { TinyColor } from '@ctrl/tinycolor';
import { css, useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import {
  EditOfferPageContext,
  PreviewDevice,
} from '@bbkAdminComponents/experiences/offers/edit-offer-page-context';
import { MobileDevicePreview } from '@bbkAdminComponents/preview-device/mobile-device';
import { type RestSpeechBubble2Modal } from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { cs } from '@bbkAdminUtils/css-utils';
import CustomerHtml from '@bbkFunnel/components/customer_html/customer_html';
import { type EmotionTheme } from '@bbkFunnel/pages/theme';
import { RefsContext } from '@bbkFunnelUtils/refs.context';
import {
  Avatar,
  ModalDesktopInnerSpacer,
  ModalMobileInnerSpacer,
  OfferTitleMobile,
  TitleTheme,
  useMediaQueries,
} from '../sections';

import './modal.scss';

type Props = {
  modalInfo: RestSpeechBubble2Modal;
  close: () => void;
  detailedAction: React.ReactElement;
};

export const SpeechBubbleV2: React.FC<Props> = ({
  modalInfo: {
    id,
    requires: {
      info: { title, details },
      person,
    },
    mobileLayout,
  },
  close,
  detailedAction,
}) => {
  const { mobileView } = useMediaQueries();
  const theme = useTheme() as EmotionTheme;
  const backgroundIsDark = new TinyColor(theme.modalBackground).isDark();
  const backgroundModalColor = new TinyColor(theme.modalBackground);
  const backgroundColor = backgroundIsDark
    ? backgroundModalColor.lighten(7).toHexString()
    : backgroundModalColor.darken(4).toHexString();

  const offerContext = useContext(EditOfferPageContext);

  const refsPrefix = id;
  const refsCtx = useContext(RefsContext);
  const titleRef = useRef<HTMLSpanElement | HTMLDivElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-title`, titleRef), [titleRef]);
  const imageRef = useRef<HTMLDivElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-image`, imageRef), [imageRef]);
  const personRef = useRef<HTMLDivElement>(null);
  useEffect(
    () => refsCtx?.addRef(`${refsPrefix}-person`, personRef),
    [personRef]
  );
  const descRef = useRef<HTMLDivElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-desc`, descRef), [descRef]);
  const actionsRef = useRef<HTMLDivElement>(null);
  useEffect(
    () => refsCtx?.addRef(`${refsPrefix}-actions`, actionsRef),
    [actionsRef]
  );

  const Details = styled.div`
    margin-bottom: 1.5rem;
    overflow-y: auto;
    max-height: 250px;
    background: ${backgroundColor};
    border-radius: 10px;
    padding: 1.5rem 1rem;
  `;

  const mobileViewContent = (
    <ModalMobileInnerSpacer
      css={{
        gridTemplateRows: 'auto minmax(1rem, auto) 1fr auto',
      }}
      className={cs('display-grid', 'mobile', 'speech-bubble')}
    >
      <OfferTitleMobile
        ref={titleRef as React.RefObject<HTMLDivElement>}
        title={<TitleTheme title={title} />}
        close={close}
      />
      <Details
        css={css`
          margin-bottom: 0px;
          max-height: unset;
        `}
        ref={descRef}
      >
        <CustomerHtml value={details} />
      </Details>
      <div
        css={css`
          padding-top: 1rem;
          padding-bottom: 1rem;
        `}
        ref={imageRef}
      >
        <Avatar
          person={person}
          includeArrow
          arrowStyles={css`
            top: calc(-5px - 1rem);
            left: 47.5px;
            transform: rotate(-90deg);
          `}
          arrowColor={backgroundColor}
        />
      </div>
      <div ref={actionsRef}>{detailedAction}</div>
    </ModalMobileInnerSpacer>
  );

  if (offerContext && offerContext?.previewDevice === PreviewDevice.MOBILE) {
    return (
      <MobileDevicePreview mobileLayout={mobileLayout}>
        {mobileViewContent}
      </MobileDevicePreview>
    );
  }

  if (mobileView) return mobileViewContent;

  return (
    <ModalDesktopInnerSpacer
      css={css`
        max-width: 650px;
      `}
      className={cs('speech-bubble', 'display-grid', 'desktop', 'inner-spacer')}
    >
      <OfferTitleMobile
        title={<TitleTheme title={title} ref={titleRef} />}
        close={close}
      />
      <div
        css={css`
          margin-top: 1rem;
        `}
        className={cs('display-grid', 'content-grid', 'align-items--center')}
      >
        <div ref={imageRef}>
          <div ref={personRef}>
            <Avatar
              person={person}
              vertical
              alignRight
              includeArrow
              arrowColor={backgroundColor}
            />
          </div>
        </div>
        <div className={cs('display-grid', 'content-items')}>
          <Details ref={descRef}>
            <CustomerHtml value={details} />
          </Details>
          <div ref={actionsRef}>{detailedAction}</div>
        </div>
      </div>
    </ModalDesktopInnerSpacer>
  );
};
